import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/*
 * Leave the approved signature editor's design and interactions intact.
 * This tiny addition lets the iframe grow with the editor on desktop/mobile,
 * avoiding a distracting nested scrollbar in the admin dashboard.
 */
const AdminEmailSignatures = () => {
  const navigate = useNavigate();
  const frameRef = useRef(null);
  const [status, setStatus] = useState("checking");
  const [frameHeight, setFrameHeight] = useState(760);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem("feral_admin_token");
    if (!token) {
      navigate("/admin/login", { replace: true });
      return;
    }

    let active = true;
    const controller = new AbortController();
    setStatus("checking");

    // Reuse an EXISTING authenticated admin endpoint. Do not alter auth
    // or introduce a new publicly accessible HTML page.
    axios.get(`${API}/admin/events`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    }).then(() => {
      if (active) setStatus("ready");
    }).catch((error) => {
      if (!active || error?.code === "ERR_CANCELED") return;
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        localStorage.removeItem("feral_admin_token");
        navigate("/admin/login", { replace: true });
      } else {
        setStatus("error");
      }
    });

    return () => { active = false; controller.abort(); };
  }, [attempt, navigate]);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin || !frameRef.current || event.source !== frameRef.current.contentWindow) return;
      if (event.data?.type !== "feral-signature-editor-height") return;
      const value = Number(event.data?.height);
      if (Number.isFinite(value)) setFrameHeight(Math.max(320, Math.min(6000, Math.ceil(value + 2))));
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  return (
    <div className="min-h-screen bg-[#f4efe5]" data-testid="admin-email-signatures">
      <nav className="bg-[#f4efe5] border-b border-[#e0d3bf] px-5 sm:px-8 py-3 flex items-center justify-between gap-4" aria-label="Admin navigation">
        <button
          type="button"
          onClick={() => navigate("/admin")}
          className="inline-flex items-center gap-2 text-[#734f37] hover:text-[#9b3d0a] text-sm font-semibold transition-colors"
        >
          <ArrowLeft size={16} /> Admin dashboard
        </button>
        <span className="text-[#8a7869] text-xs">Email signatures</span>
      </nav>

      {status === "checking" && (
        <div role="status" className="max-w-5xl mx-auto p-8 text-[#6e5646]">
          Verifying admin access…
        </div>
      )}
      {status === "error" && (
        <div className="max-w-5xl mx-auto p-8 text-[#6e5646]">
          <p>We couldn't verify your admin session. Please check your connection and try again.</p>
          <button
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            className="mt-4 bg-[#1c3c38] text-white px-4 py-2 inline-flex items-center gap-2"
          >
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      )}
      {status === "ready" && (
        <main className="w-full" aria-label="FERAL Email Signature Studio">
          <iframe
            ref={frameRef}
            title="FERAL email signature editor"
            src="/feral-signature-studio.html?v=team-members-20261027-v2"
            allow="clipboard-read; clipboard-write"
            scrolling="no"
            style={{ display: "block", width: "100%", height: frameHeight, border: 0, background: "#efe7db" }}
            data-testid="feral-signature-studio"
          />
        </main>
      )}
    </div>
  );
};

export default AdminEmailSignatures;
